// **Découper le journal des nouveautés** — et rien d'autre.
//
// Ce module est **pur** : il ne lit aucun fichier. La lecture vit dans
// [`changelog.ts`](changelog.ts), qui fait entrer `synkara/CHANGELOG.md` dans le
// bundle à la construction. La séparation n'est pas de l'élégance : le banc de
// mesure importe les modules **hors de Vite**, où `?raw` n'existe pas — un
// parseur qui lit lui-même son fichier n'aurait pas pu être mesuré du tout.

/** Une version du journal : son numéro, sa date telle qu'écrite, et son texte. */
export interface Release {
  /** `2026.09.1` — année, mois, rang dans le mois. */
  readonly version: string;
  /** La date en toutes lettres, telle qu'elle est écrite dans le journal. */
  readonly date: string;
  /** Le corps de la version, en Markdown, sans son titre. */
  readonly body: string;
}

/**
 * Le titre d'une version. Il est la **frontière** entre le fichier et l'écran :
 * ce qui ne le respecte pas n'apparaît pas, plutôt que d'apparaître de travers.
 */
const TITLE = /^##\s+(\d{4}\.\d{2}\.\d+)\s+—\s+(.+?)\s*$/;

/**
 * **Découper le journal en versions.**
 *
 * Pur, et c'est ce qui le rend mesurable sans compte ni site : l'entrée est un
 * texte, la sortie une liste. L'en-tête du fichier — ce qui précède la première
 * version — est **écarté** : il explique la numérotation à qui lit le fichier, et
 * l'écran le dit autrement.
 */
export function parseReleases(markdown: string): readonly Release[] {
  const out: Release[] = [];
  let current: { version: string; date: string; lines: string[] } | undefined;

  for (const line of markdown.split('\n')) {
    const m = TITLE.exec(line);
    if (m) {
      if (current) out.push({ version: current.version, date: current.date, body: body(current.lines) });
      current = { version: m[1]!, date: m[2]!, lines: [] };
      continue;
    }
    // Les trois tirets qui séparent deux versions appartiennent à la mise en
    // page du fichier, pas au texte d'une version : les garder poserait un filet
    // en travers de l'écran, à la fin de chaque bloc.
    if (current && line.trim() !== '---') current.lines.push(line);
  }
  if (current) out.push({ version: current.version, date: current.date, body: body(current.lines) });
  return out;
}

const body = (lines: readonly string[]): string => lines.join('\n').trim();

/**
 * **La version courante est la première du journal**, et il n'y a pas d'autre
 * endroit où elle soit écrite : un numéro tenu à deux endroits finit par être
 * faux à l'un des deux.
 */
export const versionOf = (list: readonly Release[]): string => list[0]?.version ?? '';
